import { useReducer, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectors } from 'darwin_core';
import { MODES } from 'darwin_core/utils/consts.js';
import { humanizePlusHours } from 'darwin_core/utils/helpers.js';

import BackLink from '../navigation/BackLink.jsx';
import { zonePath } from '../navigation/paths.js';
import { useZoneRouteContext } from '../navigation/ZoneRoute.jsx';
import { useZoneControls } from '../controls/useZoneControls.js';
import { overrideCopy } from '../controls/overrideCopy.js';
import { plusHoursEndText } from '../controls/plusHoursEnd.js';
import { ZoneHolidayBanner } from '../components/HolidayBanner.jsx';
import { OfflineHelp, useZoneOfflineHelp } from '../components/OfflineHelp.jsx';
import useInterval from '../hooks/useInterval.js';
import useScreen from '../hooks/useScreen.js';

// The manual-hold action screen: hold the target for a chosen duration, then fall
// back to the schedule. Same darwin_core feature (setPlusHours, hold in MINUTES)
// for both families — it is called "Plus Hours" on a central-heating zone and
// "Boost" on a hot-water zone (the `noun`). Choosing a duration and applying it
// returns to the zone; while a hold is running the screen says when it ends (the
// app shows an end time, never a countdown) and offers a cancel affordance.
const MAX_MINUTES = 6 * 60;
const STEP_MINUTES = 5;
const MIN_MINUTES = 5;
const DEFAULT_MINUTES = 120;

export default function PlusHoursScreen() {
  const { homeId, zoneId } = useZoneRouteContext();
  const navigate = useNavigate();
  const { online, inHoliday, sendable, actions } = useZoneControls(
    homeId,
    zoneId,
  );

  const name = useSelector((s) =>
    selectors.getPrtFriendlyName(s, homeId, zoneId),
  );
  const mode = useSelector((s) => selectors.getMode(s, homeId, zoneId));
  const isDhw = useSelector((s) => selectors.isDhw(s, homeId, zoneId));
  const remaining = useSelector((s) =>
    selectors.getPlusHoursTimeRemaining(s, homeId, zoneId),
  );
  const holdEnd = useSelector((s) => plusHoursEndText(s, homeId, zoneId));
  const offlineHelp = useZoneOfflineHelp(homeId, zoneId);

  // "Boost" is reserved for hot water; a heating zone's manual hold is "Plus Hours".
  const noun = isDhw ? 'Boost' : 'Plus Hours';
  const headingRef = useScreen(noun);

  // Scope "running" to an actual OverridePlus with a live remaining time — the
  // countdown selector also returns for DSRActive, which is a grid program.
  const running =
    mode === MODES.OverridePlusHours && typeof remaining === 'number';

  const [minutes, setMinutes] = useState(
    running && remaining ? remaining : DEFAULT_MINUTES,
  );

  // getPlusHoursTimeRemaining reads Date.now(); re-render on a timer so `running`
  // drops out when the hold expires (minute granularity → 30s tick).
  const [, tick] = useReducer((n) => n + 1, 0);
  useInterval(() => tick(), running ? 30000 : null);

  // The app's PlusHoursActionPanel offers a hold from Auto / OverrideNext /
  // OverridePlus only, for BOTH families — so not from Manual or Off on heating,
  // nor from On or Off on a cylinder. darwin_core's setPlusHours itself only
  // guards heating zones, but the web follows the app's gating, not the thunk's.
  const boostSupported =
    mode === MODES.Auto ||
    mode === MODES.OverrideNext ||
    mode === MODES.OverridePlusHours;

  // Holiday is explained by the ZoneHolidayBanner under the header (with the way
  // out); the hints below cover the disconnected and unsupported-mode cases. A
  // command still in flight does NOT lock the hold: darwin_core takes a new one
  // on top of it, and the Zone header spinner is what tells the user a change is
  // still on its way (the app never locked here either).
  const inputsDisabled = !sendable || !online || inHoliday;
  const startDisabled = inputsDisabled || !boostSupported;

  const setDuration = (value) =>
    setMinutes(value <= MIN_MINUTES ? MIN_MINUTES : value);

  const apply = () => {
    actions.setPlusHours(minutes);
    navigate(zonePath(homeId, zoneId));
  };

  const cancel = () => {
    actions.cancelOverride();
    navigate(zonePath(homeId, zoneId));
  };

  const description = isDhw
    ? 'Boost is immediately active. How long would you like the hot water to be on?'
    : 'Plus hours are immediately active. How long would you like to maintain the target temperature?';

  return (
    <main className="app-shell screen plus-hours-screen">
      <header className="screen__header">
        <BackLink to={zonePath(homeId, zoneId)} label={name || 'Zone'} />
        <h1 id="main-content" tabIndex={-1} ref={headingRef}>
          {noun}
        </h1>
        <p className="screen__sub">
          {isDhw
            ? 'Change your hot water status'
            : `Change your ${name || 'thermostat'}'s heating status`}
        </p>
      </header>

      <ZoneHolidayBanner
        homeId={homeId}
        zoneId={zoneId}
        detail={`Cancel Holiday to start ${noun}.`}
      />

      {/* Why the duration and Apply below are dead when the zone cannot be
          reached: the app's own words for the link that is down, in place of a
          hint of our own. */}
      {offlineHelp.show && <OfflineHelp {...offlineHelp} />}

      <section className="plus-hours">
        <p className="plus-hours__desc">{description}</p>

        {running && (
          <p className="plus-hours__countdown" aria-live="polite">
            {holdEnd ? `${noun} active until ${holdEnd}` : `${noun} active`}
          </p>
        )}

        {/* No aria-live here — the slider's aria-valuetext already announces the
            duration, so a live region would double every step for SR users. */}
        <div className="plus-hours__value">
          + {humanizePlusHours(minutes)} hrs
        </div>

        <input
          type="range"
          className="plus-hours__slider"
          // The real floor is MIN_MINUTES (setDuration clamps to it); using it as
          // the slider min keeps the exposed aria-valuemin honest and stops a drag
          // below the floor, rather than min=0 with a silent post-clamp.
          min={MIN_MINUTES}
          max={MAX_MINUTES}
          step={STEP_MINUTES}
          value={minutes}
          disabled={inputsDisabled}
          aria-label={`${noun} duration`}
          aria-valuetext={`${humanizePlusHours(minutes)} hours`}
          onChange={(e) => setDuration(Number(e.target.value))}
        />
        <div className="plus-hours__scale" aria-hidden="true">
          <span>0</span>
          <span>6</span>
        </div>

        <button
          type="button"
          className="plus-hours__apply"
          disabled={startDisabled}
          onClick={apply}
        >
          {running ? 'Save changes' : `Apply ${noun}`}
        </button>

        {running && online && (
          <button type="button" className="plus-hours__cancel" onClick={cancel}>
            {overrideCopy(mode, isDhw).cancel}
          </button>
        )}

        {/* Both are silent while the offline help stands above: it already says
            why nothing here can be started, and the mode it reports is only the
            last one the zone got to report. */}
        {!offlineHelp.show &&
          (!sendable ? (
            <p className="plus-hours__hint">
              You appear to be offline — reconnect to start {noun}.
            </p>
          ) : (
            !boostSupported && (
              <p className="plus-hours__hint">
                Switch to Auto to start {noun}.
              </p>
            )
          ))}
      </section>
    </main>
  );
}
